// @givmo/app-admin — the Steps Sponsor DPA click-through on the CREATE form (SC-GO2).
//
// Creating a competition binds the company as Givfi's PROCESSOR of participant health data, so an
// unbound company accepts the Addendum here, before the first competition exists. Three rails shape
// every branch (the shipped iOS admin create screen carries the same three):
//
//   1. NEVER BIND A COMPANY TO TEXT IT HAS NOT BEEN SHOWN. The checkbox exists only in the `required`
//      gate, which by construction carries a readable body. When the state could not be read — or
//      came back bodyless — the requirement is stated honestly and NO acceptance is collected.
//   2. NEVER WEDGE THE ADMIN. `unreadable` does not block Create: the create goes up without the
//      field and the SERVER decides — an already-bound company creates normally, an unbound one
//      draws the 422 that hands back the Addendum and turns the failed read into a real
//      click-through.
//   3. THE TICK IS THE BINDING ACT. Unbundled from every other control, never pre-checked, and the
//      Create button stays disabled until it is set.
//
// The document renders AS DELIVERED — the same bytes whose hash the acceptance row pins server-side.
// Inline emphasis only; block syntax stays literal so no parser can restructure a legal instrument
// (see `sponsorDpaParagraphs` / `sponsorDpaSpans`).

import { Fragment, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Dialog,
  testIdProp,
} from '@givmo/design-system';
import {
  sponsorDpaParagraphs,
  sponsorDpaSpans,
  type SponsorDpaGate,
  type SponsorDpaState,
} from './competitions-decode';

const PREFIX = 'competitions-form-dpa';

export const DPA_TITLE = 'Data Processing Addendum';

/** First-acceptance framing vs. a version that became effective after a prior acceptance. */
const FIRST_ACCEPTANCE_COPY =
  'Competition admins at your company can see participants’ daily step totals. Before your first ' +
  'competition, your company accepts the addendum that governs how it may handle that data.';

const NEW_VERSION_COPY =
  'A new version of the addendum that governs how your company may handle participants’ daily step ' +
  'totals has taken effect. Review and accept it to create a competition.';

const UNAVAILABLE_COPY =
  'Your company must accept the Steps Competition Data Processing Addendum before its first ' +
  'competition. We couldn’t load it just now — you can retry, or continue and we’ll ask again if ' +
  'it’s needed.';

export const DPA_ACCEPT_LABEL = 'I accept the Data Processing Addendum on behalf of my company.';

export interface SponsorDpaSectionProps {
  gate: SponsorDpaGate;
  /** Whether the acceptance box is ticked. Owned by the form so submit can gate on it. */
  accepted: boolean;
  onAcceptedChange: (accepted: boolean) => void;
  /** Re-read the state. Offered only in the `unreadable` gate. */
  onRetry: () => void;
  retrying: boolean;
  disabled: boolean;
}

/** Renders nothing when the company is already bound to the current version. */
export function SponsorDpaSection({
  gate,
  accepted,
  onAcceptedChange,
  onRetry,
  retrying,
  disabled,
}: SponsorDpaSectionProps) {
  if (gate.kind === 'satisfied') return null;

  return (
    <Card testId={PREFIX}>
      <CardHeader title="Data processing" />
      <CardBody className="flex flex-col gap-12">
        {gate.kind === 'required' ? (
          <AcceptanceRequired
            state={gate.state}
            accepted={accepted}
            onAcceptedChange={onAcceptedChange}
            disabled={disabled}
          />
        ) : (
          <>
            <p className="text-s13 text-text-secondary" {...testIdProp(`${PREFIX}-unavailable`)}>
              {UNAVAILABLE_COPY}
            </p>
            <div>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                loading={retrying}
                onClick={onRetry}
                testId={`${PREFIX}-retry`}
              >
                Try again
              </Button>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function AcceptanceRequired({
  state,
  accepted,
  onAcceptedChange,
  disabled,
}: {
  state: SponsorDpaState;
  accepted: boolean;
  onAcceptedChange: (accepted: boolean) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <p className="text-s13 text-text-secondary">
        {state.accepted ? NEW_VERSION_COPY : FIRST_ACCEPTANCE_COPY}
      </p>

      <div>
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          onClick={() => setOpen(true)}
          testId={`${PREFIX}-open`}
        >
          Read the {DPA_TITLE}
        </Button>
      </div>

      {/*
        Unbundled and never pre-checked: the tick is the company's binding act and nothing else.
        The wrapping <label> IS the accessible name (Base UI points `aria-labelledby` at it), so no
        `aria-label` here — adding one stacks on top of the label text and a screen reader announces
        the affirmation twice.
      */}
      <label className="flex cursor-pointer items-start gap-10">
        <Checkbox
          appearance="saas"
          checked={accepted}
          onCheckedChange={onAcceptedChange}
          disabled={disabled}
          className="mt-2"
          testId={`${PREFIX}-accept`}
        />
        <span className="text-s14 text-text-secondary">{DPA_ACCEPT_LABEL}</span>
      </label>

      <SponsorDpaDialog state={state} open={open} onOpenChange={setOpen} />
    </>
  );
}

function SponsorDpaDialog({
  state,
  open,
  onOpenChange,
}: {
  state: SponsorDpaState;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        aria-label={DPA_TITLE}
        width={720}
        className="flex max-h-[80vh] flex-col gap-12"
        testId={`${PREFIX}-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {DPA_TITLE}
        </Dialog.Title>
        {state.currentVersionNumber > 0 && (
          <Dialog.Description className="text-s13 text-text-tertiary">
            Version {state.currentVersionNumber}
            {state.currentEffectiveAt !== null &&
              ` · effective ${formatEffective(state.currentEffectiveAt)}`}
          </Dialog.Description>
        )}

        <div
          className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-border-subtle bg-surface-sunken p-16"
          {...testIdProp(`${PREFIX}-body`)}
        >
          {sponsorDpaParagraphs(state.content).map((paragraph, index) => (
            <p key={index} className="mb-10 text-s13 leading-h15 text-text-strong last:mb-0">
              {sponsorDpaSpans(paragraph).map((span, spanIndex) => (
                <Fragment key={spanIndex}>
                  {span.bold ? <strong>{span.text}</strong> : span.text}
                </Fragment>
              ))}
            </p>
          ))}
        </div>

        <div className="flex items-center justify-end">
          <Dialog.Close
            render={<Button appearance="saas" tone="secondary" size="md" />}
            {...testIdProp(`${PREFIX}-dialog-close`)}
          >
            Done
          </Dialog.Close>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/** The effective stamp is cosmetic — an unparseable instant renders as delivered, never as junk. */
function formatEffective(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return EFFECTIVE_FORMAT.format(date);
}

const EFFECTIVE_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});
